import React from 'react'
import { ModerationComponentProps } from './moderation.constants'
import { useModerationStyles } from './moderation.styles'
import { Grid } from '@material-ui/core'
import { ListedItem, ListedItemProps } from '../../../components/listed-item'
import { AppPagination } from '../../../components/app-pagination'
import { ApplyType } from '../../../shared/filter/filter.constants'
import { EmptySearchResultComponent } from '../../../components/empty-search-result-component'
import { PlaceholderArrayComponent } from '../../../components/placeholder-array-component'
import { useScreenSize } from '../../../hooks/use-screen-size'
import { useTranslation } from 'react-i18next'
import { ItemState } from '../../../models'
import { ModerationFilter } from '../../../components/moderation-filters'
import { ModerationFilterParamsType } from '../../../components/moderation-filters/moderation-filter.constants'
import { PageWrapper } from '../../../components/page-wrapper'
import { BreadcrumbItem } from '../../../components/breadcrumbs'
import { ShowItemsSelect } from '../../../components/show-items-select'
import { ScreenSize } from '../../../settings/device'
import { GridItem } from '../../../shared/grid-item'

export const Moderation: React.FC<ModerationComponentProps> = (
  props: ModerationComponentProps
) => {
  const {
    itemsList,
    query,
    changeQueryParamsAction,
    totalPages,
    loading,
    firstRender,
    cancelHandler,
    isFilterCollapsed,
    changeFilterCollapsedHandler,
  } = props

  const classes = useModerationStyles()

  const { t } = useTranslation()

  /*Device Type*/
  const screenSize = useScreenSize()

  /*Filter Change Handler*/
  const onFilterChanged = (
    data: ModerationFilterParamsType,
    apply?: ApplyType,
    isStartPagingInBegin?: boolean
  ) => {
    changeQueryParamsAction(data, apply, isStartPagingInBegin)
  }

  /*Pagination Change Handler*/
  const onPaginationHandler = (pageIndex: number) => {
    changeQueryParamsAction(
      {
        pageIndex,
      },
      true
    )
  }

  const moderationBreadcrumbItems: BreadcrumbItem[] = [
    { label: t('header.menu.search'), to: '/search' },
    { label: t('header.menu.moderation') },
  ]

  const onPageSizeHandler = (pageSize: number) => {
    changeQueryParamsAction(
      {
        pageSize,
      },
      true,
      true
    )
  }

  const renderFilter = () => (
    <ModerationFilter
      onFilterChanged={onFilterChanged}
      loading={loading}
      cancelHandler={cancelHandler}
      isFilterCollapsed={isFilterCollapsed}
      setFilterCollapsed={changeFilterCollapsedHandler}
      screenSize={screenSize}
      moderationState={query.moderationState}
    />
  )

  return (
    <PageWrapper
      title={t('header.menu.moderation')}
      displayGoBackButton={false}
      breadcrumbItems={moderationBreadcrumbItems}
      titleEndContent={
        screenSize === ScreenSize.Desktop ? (
          <ShowItemsSelect
            value={query.pageSize}
            onChange={onPageSizeHandler}
            disabled={loading}
            label={t('moderation.showItems')}
            idSuffix="moderation-desktop"
          />
        ) : undefined
      }
    >
      <Grid container spacing={1}>
        {screenSize !== ScreenSize.Desktop && (
          <Grid item xs={12}>
            <div className={classes.mobileControls}>
              <div className={classes.mobileFilterControl}>
                {renderFilter()}
              </div>
              <ShowItemsSelect
                value={query.pageSize}
                onChange={onPageSizeHandler}
                disabled={loading}
                label={t('moderation.showItems')}
                idSuffix="moderation-mobile"
                className={classes.mobileShowItemsSelect}
              />
            </div>
          </Grid>
        )}
        {screenSize === ScreenSize.Desktop && (
          <Grid item xs={12} lg={3} xl={2} md={3}>
            <div className={classes.filterContainer}>{renderFilter()}</div>
          </Grid>
        )}
        <Grid item xs={12} xl={10} lg={9} md={9}>
          <Grid container spacing={1}>
            {firstRender && <PlaceholderArrayComponent />}
            {!itemsList.length && !firstRender && (
              <Grid xs={12} item>
                <EmptySearchResultComponent
                  title={t('emptySearchResult.title')}
                  description={t('emptySearchResult.description')}
                />
              </Grid>
            )}
            {itemsList.map((item: ListedItemProps, key: number) => (
              <GridItem key={key} xxxs={12} xxs={6} xs={4} md={4} lg={3} xl={2}>
                <ListedItem
                  {...item}
                  state={
                    item.state === ItemState.Active ? undefined : item.state
                  }
                  isModerator={true}
                />
              </GridItem>
            ))}
            {!firstRender && totalPages > 1 && (
              <Grid item xs={12}>
                <AppPagination
                  spacing="minimal"
                  count={totalPages}
                  disabled={loading}
                  page={query.pageIndex}
                  onChange={(e, page) => onPaginationHandler(page)}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </PageWrapper>
  )
}
